import { Search, Users, Shield as ShieldIcon, Sparkles, Unlock, MessageCircle, MoreHorizontal, Trash2, UserX, Moon, Send } from 'lucide-react';
import { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Plus, Minus, Star, KeyRound, Shield, Pencil, Gift } from 'lucide-react';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
    DropdownMenuSeparator, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { useShowMore } from '../../components/useShowMore';
import { usePlayerDrilldown } from '../../components/PlayerDrilldownContext';
import EditUserDialog from '../../components/EditUserDialog';
import DangerZoneDialog from '../../components/DangerZoneDialog';
import FreePlayQuickDialog from '../../components/FreePlayQuickDialog';
import MoneyFlowByMethodCard from '../../components/LoadsByMethodCard';
import TagUnspecifiedDialog from '../../components/TagUnspecifiedDialog';
import AppAdoptionPanel from '../../components/admin/AppAdoptionPanel';
import EmailCollectionPanel from '../../components/admin/EmailCollectionPanel';
import { useMessagePlayer } from '../../contexts/MessagePlayerContext';

// An ambassador is a real player who ALSO refers others ("one account, two
// doors"). Treat them as a player everywhere in this table so their credits,
// wagering and player actions show up — they are NOT a backoffice account.
const isPlayer = (u) => u.role === 'user' || u.role === 'ambassador';

// Players who joined in the last 48h are considered "new". Anything missing a
// `created_at` is treated as old to avoid false positives.
const NEW_PLAYER_WINDOW_MS = 48 * 60 * 60 * 1000;
const isNewPlayer = (u) => {
    if (!isPlayer(u) || !u.created_at) return false;
    const created = new Date(u.created_at).getTime();
    if (Number.isNaN(created)) return false;
    return Date.now() - created <= NEW_PLAYER_WINDOW_MS;
};

const formatRelative = (iso) => {
    if (!iso) return '—';
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return '—';
    const diff = Date.now() - t;
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}d ago`;
    return new Date(iso).toLocaleDateString('en-US', { timeZone: 'America/Chicago' });
};

// Recently-wiped predicate — accounts that had a data wipe within the last
// 30 days and have NOT been restored. Lets admins triage/restore fresh
// wipes without hunting through the whole roster.
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
const isRecentlyWiped = (u) => {
    if (!u.wiped_at || u.restored_at) return false;
    const ts = new Date(u.wiped_at).getTime();
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts <= THIRTY_DAYS_MS;
};

// Players who have NEVER loaded (deposited). The /admin/users list only sets
// last_load_at when a real load exists, so no last_load_at = never deposited.
const isNeverLoaded = (u) => isPlayer(u) && !u.last_load_at;

// Players who DID load before but have gone quiet — last real load is older
// than 30 days. These are win-back / re-engagement targets.
const isDormant30d = (u) => {
    if (!isPlayer(u) || !u.last_load_at) return false;
    const ts = new Date(u.last_load_at).getTime();
    if (!ts) return false;
    return Date.now() - ts > THIRTY_DAYS_MS;
};

const FILTERS = [
    { id: 'new',        label: 'New (48h)',   icon: Sparkles,   color: '#FF6B00', predicate: isNewPlayer },
    { id: 'players',    label: 'Players',     icon: Users,      color: '#39FF14', predicate: u => isPlayer(u) },
    { id: 'noload',     label: 'No Deposit',  icon: UserX,      color: '#FF3B30', predicate: isNeverLoaded },
    { id: 'dormant',    label: 'Quiet 30d',   icon: Moon,       color: '#00E5FF', predicate: isDormant30d },
    { id: 'backoffice', label: 'Backoffice',  icon: ShieldIcon, color: '#FF4DB8', predicate: u => !isPlayer(u) },
    { id: 'wiped',      label: 'Wiped (30d)', icon: Trash2,     color: '#FFB84D', predicate: isRecentlyWiped },
    { id: 'all',        label: 'All',         icon: null,       color: '#FFD700', predicate: () => true },
];

export const UsersAdminTab = ({
    searchTerm, setSearchTerm,
    loading, filteredUsers,
    getStatusBadge,
    openCreditDialog,
    setSelectedUser, setFreeSpinsDialogOpen,
    setResetPwDialogOpen, setStatusDialogOpen,
    fetchData,
}) => {
    const [roleFilter, setRoleFilter] = useState('players');
    const [ambFilter, setAmbFilter] = useState('');
    const [ambList, setAmbList] = useState([]);
    const API = process.env.REACT_APP_BACKEND_URL + '/api';
    // Bulk "message this cohort" (No Deposit / Quiet 30d win-back nudges)
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkTitle, setBulkTitle] = useState('');
    const [bulkMsg, setBulkMsg] = useState('');
    const [bulkSending, setBulkSending] = useState(false);
    const [resultsOpen, setResultsOpen] = useState(false);
    const [campaigns, setCampaigns] = useState([]);
    const [loadingResults, setLoadingResults] = useState(false);
    const { open: openMessageDialog } = useMessagePlayer();
    const { openPlayer } = usePlayerDrilldown();
    const [tagDialogOpen, setTagDialogOpen] = useState(false);
    const [tagKind, setTagKind] = useState('load');
    const [moneyFlowRefresh, setMoneyFlowRefresh] = useState(0);

    const openTagDialog = (kind) => { setTagKind(kind); setTagDialogOpen(true); };

    // Load ambassador list once for the "referred by" filter dropdown.
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data } = await axios.get(`${API}/admin/ambassadors`, { withCredentials: true });
                if (alive) setAmbList(data.rows || []);
            } catch (e) { /* non-fatal */ }
        })();
        return () => { alive = false; };
    }, [API]);

    // Honor a preset filter when deep-linked from the revenue-summary tiles.
    useEffect(() => {
        try {
            const preset = sessionStorage.getItem('admin_players_amb_filter');
            if (preset) {
                setAmbFilter(preset);
                setRoleFilter('all');
                sessionStorage.removeItem('admin_players_amb_filter');
            }
        } catch { /* ignore */ }
    }, []);

    const handleUnlockLogin = async (u) => {
        try {
            await axios.post(
                `${API}/admin/unlock-login`,
                { user_id: u.id, new_password: 'ignored' },
                { withCredentials: true },
            );
            toast.success(`Login unlocked for ${u.name}`);
            fetchData?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to unlock login');
        }
    };

    // Counts are computed once per filteredUsers change for badge display
    const counts = useMemo(() => ({
        new:        filteredUsers.filter(isNewPlayer).length,
        players:    filteredUsers.filter(isPlayer).length,
        noload:     filteredUsers.filter(isNeverLoaded).length,
        dormant:    filteredUsers.filter(isDormant30d).length,
        backoffice: filteredUsers.filter(u => !isPlayer(u)).length,
        wiped:      filteredUsers.filter(isRecentlyWiped).length,
        all:        filteredUsers.length,
    }), [filteredUsers]);

    const visible = useMemo(() => {
        const pred = FILTERS.find(f => f.id === roleFilter)?.predicate || (() => true);
        let list = filteredUsers.filter(pred);
        if (ambFilter === '__house__') {
            list = list.filter(u => u.ref_is_house);
        } else if (ambFilter === '__amb__') {
            list = list.filter(u => u.ref_ambassador_id && !u.ref_is_house);
        } else if (ambFilter) {
            list = list.filter(u => u.ref_ambassador_id === ambFilter);
        }
        // For the "new"/"No Deposit" views, surface the freshest signups first
        // (newest leads to chase at the top).
        if (roleFilter === 'new' || roleFilter === 'noload') {            return [...list].sort((a, b) => {
                const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
                const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
                return tb - ta;
            });
        }
        // For the "wiped" view, surface the most recently wiped first.
        if (roleFilter === 'wiped') {
            return [...list].sort((a, b) => {
                const ta = a.wiped_at ? new Date(a.wiped_at).getTime() : 0;
                const tb = b.wiped_at ? new Date(b.wiped_at).getTime() : 0;
                return tb - ta;
            });
        }
        // For the "Quiet 30d" view, surface the longest-quiet players first
        // (oldest last-load = most overdue for a win-back nudge).
        if (roleFilter === 'dormant') {
            return [...list].sort((a, b) => {
                const ta = a.last_load_at ? new Date(a.last_load_at).getTime() : 0;
                const tb = b.last_load_at ? new Date(b.last_load_at).getTime() : 0;
                return ta - tb;
            });
        }
        return list;
    }, [filteredUsers, roleFilter, ambFilter]);

    const { visibleItems: paginated, ShowMoreButton, total, limit } = useShowMore(visible, 10, 10);

    const sendBulkMessage = async () => {
        if (!bulkTitle.trim() || !bulkMsg.trim()) { toast.error('Add a title and a message'); return; }
        const ids = visible.map(u => u.id).filter(Boolean);
        if (ids.length === 0) { toast.error('No players in this list'); return; }
        setBulkSending(true);
        try {
            const { data } = await axios.post(`${API}/admin/message/broadcast`, {
                title: bulkTitle.trim(), message: bulkMsg.trim(), user_ids: ids,
                cohort: cohortLabel || roleFilter,
            }, { withCredentials: true });
            toast.success(data?.message || `Sent to ${ids.length} players`);
            setBulkOpen(false); setBulkTitle(''); setBulkMsg('');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to send');
        }
        setBulkSending(false);
    };
    const cohortLabel = roleFilter === 'noload' ? 'No-Deposit' : roleFilter === 'dormant' ? 'Quiet-30d' : '';
    const showBulkBar = (roleFilter === 'noload' || roleFilter === 'dormant') && visible.length > 0;
    const openResults = async () => {
        setResultsOpen(true); setLoadingResults(true);
        try {
            const { data } = await axios.get(`${API}/admin/nudge-campaigns`, { withCredentials: true });
            setCampaigns(data?.campaigns || []);
        } catch { setCampaigns([]); }
        setLoadingResults(false);
    };

    return (
    <div className="card-dark p-6">
        {/* Platform-wide money flow by method (today, CT). Auto-refreshes every 30s. */}
        <div className="mb-4" data-testid="admin-money-flow">
            <MoneyFlowByMethodCard
                loadsEndpoint="/admin/loads-by-method-today"
                cashoutsEndpoint="/admin/cashouts-by-method-today"
                loadsTitle="Today's Loads by Method (Platform)"
                cashoutsTitle="Today's Cashouts by Method (Platform)"
                onTagLoadsClick={() => openTagDialog('load')}
                onTagCashoutsClick={() => openTagDialog('cashout')}
                refreshKey={moneyFlowRefresh}
            />
        </div>
        <TagUnspecifiedDialog
            open={tagDialogOpen}
            onOpenChange={setTagDialogOpen}
            kind={tagKind}
            onDone={() => { setMoneyFlowRefresh(k => k + 1); fetchData?.(); }}
        />
        {/* App-adoption tracker: who has notifications on / added to home screen */}
        <AppAdoptionPanel />
        <EmailCollectionPanel />
        <div className="flex flex-col sm:flex-row gap-3 mb-4 sticky top-0 z-30 bg-[#121212] -mx-6 px-6 py-3 border-b border-white/5 backdrop-blur supports-[backdrop-filter]:bg-[#121212]/95" data-testid="players-sticky-search">
            <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A0AAB2]" />
                <Input
                    placeholder="Search by name, email, or phone..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-[#0a0a0a] border-white/10 text-white"
                    data-testid="user-search"
                />
            </div>
            <div className="flex items-center gap-1 bg-[#0a0a0a] border border-white/10 rounded-lg p-1"
                 data-testid="role-filter-group">
                {FILTERS.map(f => {
                    const active = roleFilter === f.id;
                    const Icon = f.icon;
                    return (
                        <button key={f.id}
                            onClick={() => setRoleFilter(f.id)}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition"
                            style={{
                                background: active ? `${f.color}22` : 'transparent',
                                color: active ? f.color : '#A0AAB2',
                                border: active ? `1px solid ${f.color}55` : '1px solid transparent',
                            }}
                            data-testid={`role-filter-${f.id}`}>
                            {Icon && <Icon className="w-3.5 h-3.5" />}
                            {f.label}
                            <span className="ml-0.5 text-[10px] font-mono px-1 rounded"
                                  style={{ background: active ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.05)' }}>
                                {counts[f.id]}
                            </span>
                        </button>
                    );
                })}
            </div>
            <div className="flex items-center gap-2 bg-[#0a0a0a] border border-white/10 rounded-lg px-2 py-1">
                <span className="text-[10px] uppercase tracking-wider text-[#A0AAB2] font-bold">Ambassador</span>
                <select
                    value={ambFilter}
                    onChange={(e) => setAmbFilter(e.target.value)}
                    className="bg-transparent text-xs text-white outline-none max-w-[160px]"
                    data-testid="users-ambassador-filter"
                >
                    <option value="" className="bg-[#121212]">All</option>
                    <option value="__house__" className="bg-[#121212]">★ House players</option>
                    <option value="__amb__" className="bg-[#121212]">★ Ambassador-referred</option>
                    {ambList.map((a) => (
                        <option key={a.id} value={a.id} className="bg-[#121212]">{a.name} ({a.code})</option>
                    ))}
                </select>
                {ambFilter && (
                    <button onClick={() => setAmbFilter('')} className="text-[#A0AAB2] hover:text-white text-xs"
                        data-testid="users-ambassador-filter-clear" title="Clear">✕</button>
                )}
            </div>
        </div>

        {showBulkBar && (
            <div className="flex items-center justify-between gap-3 mb-3 rounded-lg border px-4 py-2.5"
                 style={{ borderColor: roleFilter === 'noload' ? '#FF3B3055' : '#00E5FF55', background: roleFilter === 'noload' ? '#FF3B3010' : '#00E5FF10' }}
                 data-testid="bulk-message-bar">
                <p className="text-xs text-white">
                    {roleFilter === 'noload'
                        ? <><span className="font-black text-[#FF3B30]">{visible.length}</span> players have never deposited — send them a first-load nudge.</>
                        : <><span className="font-black text-[#00E5FF]">{visible.length}</span> players loaded before but have been quiet 30+ days — win them back.</>}
                </p>
                <div className="flex items-center gap-2 shrink-0">
                    <Button size="sm" variant="outline" onClick={openResults}
                        className="border-white/15 text-[#A0AAB2] hover:text-white" data-testid="nudge-results-btn">
                        📊 Results
                    </Button>
                    <Button size="sm" onClick={() => setBulkOpen(true)}
                        className="font-bold text-black"
                        style={{ background: roleFilter === 'noload' ? '#FF3B30' : '#00E5FF' }}
                        data-testid="bulk-message-open-btn">
                        <MessageCircle className="w-4 h-4 mr-1" /> Message all {visible.length}
                    </Button>
                </div>
            </div>
        )}

        {bulkOpen && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4" data-testid="bulk-message-modal"
                 onClick={(e) => { if (e.target === e.currentTarget && !bulkSending) setBulkOpen(false); }}>
                <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#121212] p-5 space-y-3">
                    <div>
                        <h3 className="text-lg font-black text-white">Message {cohortLabel} players</h3>
                        <p className="text-xs text-[#A0AAB2]">In-app notification (bell) to all <span className="text-white font-bold">{visible.length}</span> players in this filter.</p>
                    </div>
                    <Input value={bulkTitle} onChange={(e) => setBulkTitle(e.target.value)} maxLength={80}
                        placeholder="Title (e.g. We miss you! 🎁)" className="bg-[#0a0a0a] border-white/10 text-white" data-testid="bulk-message-title" />
                    <textarea value={bulkMsg} onChange={(e) => setBulkMsg(e.target.value)} maxLength={500} rows={4}
                        placeholder={roleFilter === 'noload' ? 'e.g. Make your first load today and get Free Play — tap to play!' : 'e.g. Come back for a fresh Free Play bonus on your next load!'}
                        className="w-full rounded-lg bg-[#0a0a0a] border border-white/10 text-white text-sm p-3 resize-none" data-testid="bulk-message-body" />
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setBulkOpen(false)} disabled={bulkSending} className="border-white/15 text-white" data-testid="bulk-message-cancel">Cancel</Button>
                        <Button onClick={sendBulkMessage} disabled={bulkSending} className="bg-[#39FF14] text-black font-black" data-testid="bulk-message-send">
                            <Send className="w-4 h-4 mr-1" /> {bulkSending ? 'Sending…' : `Send to ${visible.length}`}
                        </Button>
                    </div>
                </div>
            </div>
        )}

        {resultsOpen && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4" data-testid="nudge-results-modal"
                 onClick={(e) => { if (e.target === e.currentTarget) setResultsOpen(false); }}>
                <div className="w-full max-w-2xl rounded-2xl border border-white/10 bg-[#121212] p-5 space-y-3 max-h-[85vh] overflow-y-auto">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-lg font-black text-white">Nudge Results</h3>
                            <p className="text-xs text-[#A0AAB2]">Players who loaded within 48h of each nudge.</p>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => setResultsOpen(false)} className="border-white/15 text-white" data-testid="nudge-results-close">Close</Button>
                    </div>
                    {loadingResults ? (
                        <div className="py-8 text-center text-[#A0AAB2] text-sm">Loading…</div>
                    ) : campaigns.length === 0 ? (
                        <div className="py-8 text-center text-[#A0AAB2] text-sm">No nudges sent yet. Send one from a cohort above.</div>
                    ) : (
                        <div className="space-y-2" data-testid="nudge-results-list">
                            {campaigns.map(c => (
                                <div key={c.id} className="rounded-lg border border-white/10 bg-[#0a0a0a] px-3 py-2.5">
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="min-w-0">
                                            <p className="text-sm font-bold text-white truncate">{c.title}</p>
                                            <p className="text-[10px] text-[#A0AAB2]">
                                                {c.cohort} · {c.sent_at ? new Date(c.sent_at).toLocaleDateString() : ''} · sent to {c.sent_count}
                                            </p>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <p className="text-lg font-black leading-none" style={{ color: c.conversion_rate >= 20 ? '#39FF14' : c.conversion_rate > 0 ? '#FFD700' : '#A0AAB2' }}>
                                                {c.conversion_rate}%
                                            </p>
                                            <p className="text-[10px] text-[#A0AAB2]">{c.converted}/{c.sent_count} loaded · ${c.loaded_total}</p>
                                        </div>
                                    </div>
                                    <p className="text-[11px] text-[#A0AAB2] mt-1 line-clamp-1 italic">"{c.message}"</p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        )}

        {loading ? (
            <div className="flex justify-center py-12"><div className="spinner" /></div>
        ) : (
            <>
            <div className="overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow className="border-white/10">
                            <TableHead className="text-[#A0AAB2]">User</TableHead>
                            <TableHead className="text-[#A0AAB2]">Status</TableHead>
                            <TableHead className="text-[#A0AAB2]">Role</TableHead>
                            <TableHead className="text-[#A0AAB2] text-right">Credits</TableHead>
                            <TableHead className="text-[#A0AAB2] text-right">Wagered</TableHead>
                            <TableHead className="text-[#A0AAB2]">Joined</TableHead>
                            <TableHead className="text-[#A0AAB2] text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {visible.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} className="text-center py-8 text-[#A0AAB2] text-sm">
                                    {roleFilter === 'new'
                                        ? 'No new signups in the last 48 hours yet.'
                                        : `No ${roleFilter === 'backoffice' ? 'backoffice' : roleFilter === 'players' ? 'players' : 'users'} match the current filters.`}
                                </TableCell>
                            </TableRow>
                        )}
                        {paginated.map((u, i) => (
                            <TableRow key={u.id} className="border-white/10" data-testid={`user-row-${i}`}>
                                <TableCell>
                                    <div>
                                        <p className="text-white font-medium flex items-center gap-1.5 flex-wrap">
                                            {isPlayer(u) ? (
                                                <button
                                                    type="button"
                                                    onClick={() => openPlayer(u.id || u._id)}
                                                    className="text-white hover:text-[#FFD700] underline decoration-dotted decoration-[#A0AAB2]/40 underline-offset-2 hover:decoration-[#FFD700] transition-colors text-left"
                                                    title="View player report (transactions + info)"
                                                    data-testid={`name-report-${i}`}
                                                >
                                                    {u.name}
                                                </button>
                                            ) : (
                                                <span>{u.name}</span>
                                            )}
                                            {u.role === 'ambassador' && (
                                                <span
                                                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#FF4DB8]/15 text-[#FF4DB8] border border-[#FF4DB8]/30"
                                                    title="This player is also an ambassador (one account, two doors)"
                                                    data-testid={`ambassador-badge-${i}`}
                                                >
                                                    AMBASSADOR
                                                </span>
                                            )}
                                            {Number(u.pending_rollover || 0) > 0 && (
                                                <span
                                                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#FFD700]/15 text-[#FFD700] border border-[#FFD700]/30"
                                                    title={`Bonus rollover pending: $${Number(u.pending_rollover).toFixed(2)} left to wager`}
                                                    data-testid={`rollover-badge-${i}`}
                                                >
                                                    🎁 ${Number(u.pending_rollover).toFixed(2)}
                                                </span>
                                            )}
                                        </p>
                                        <p className="text-[#A0AAB2] text-xs">{u.email}</p>
                                    </div>
                                </TableCell>
                                <TableCell>{getStatusBadge(u.status || 'active')}</TableCell>
                                <TableCell>
                                    <span className={`px-2 py-1 rounded-full text-xs font-bold uppercase ${
                                        u.role === 'admin' ? 'bg-[#FFD700]/20 text-[#FFD700]' : 'bg-white/10 text-[#A0AAB2]'
                                    }`}>{u.role}</span>
                                </TableCell>
                                <TableCell className="text-right font-mono text-[#00FFFF]">
                                    {!isPlayer(u) ? <span className="text-[#666] text-xs italic">—</span> : (u.credits || 0).toFixed(2)}
                                </TableCell>
                                <TableCell className="text-right font-mono text-[#A0AAB2]">
                                    {!isPlayer(u) ? <span className="text-[#666] text-xs italic">—</span> : (u.total_wagered || 0).toFixed(2)}
                                </TableCell>
                                <TableCell className="text-[#A0AAB2] text-xs whitespace-nowrap">
                                    {u.created_at ? (
                                        <span title={new Date(u.created_at).toLocaleString('en-US', { timeZone: 'America/Chicago', weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) + ' CT'}>
                                            {formatRelative(u.created_at)}
                                            {isNewPlayer(u) && (
                                                <span className="ml-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#FF6B00]/15 text-[#FF6B00] border border-[#FF6B00]/30">
                                                    NEW
                                                </span>
                                            )}
                                        </span>
                                    ) : '—'}
                                </TableCell>
                                <TableCell className="text-right">
                                    <div className="flex items-center justify-end gap-1">
                                        {isPlayer(u) && isRecentlyWiped(u) ? (
                                            // Wiped-recent users get a single dedicated Restore button
                                            // — deposits/messages don't make sense until the account
                                            // has been reactivated with goodwill credit.
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={async () => {
                                                    const ok = window.confirm(
                                                        `Restore ${u.name || u.email}?\n\n` +
                                                        `This will credit $10 goodwill Free Play and mark the account restored. Cannot be undone.`,
                                                    );
                                                    if (!ok) return;
                                                    try {
                                                        const { data } = await axios.post(
                                                            `${API}/admin/wiped-players/restore`,
                                                            {
                                                                user_ids: [u.id || u._id],
                                                                amount_per_player: 10,
                                                                confirm: 'RESTORE',
                                                                note: 'Row-level restore from Wiped(30d) filter',
                                                            },
                                                            { withCredentials: true },
                                                        );
                                                        toast.success(
                                                            `Restored ${data?.restored_count || 0} account${data?.restored_count === 1 ? '' : 's'} · $${Number(data?.total_credited || 0).toFixed(2)} credited`,
                                                        );
                                                        fetchData();
                                                    } catch (err) {
                                                        toast.error(err?.response?.data?.detail || 'Restore failed');
                                                    }
                                                }}
                                                className="border-[#FFB84D]/50 text-[#FFB84D] hover:bg-[#FFB84D]/10 h-8 px-3"
                                                data-testid={`restore-wiped-btn-${i}`}
                                                title="Restore this account with $10 goodwill Free Play"
                                            >
                                                <Trash2 className="w-3.5 h-3.5 mr-1" />
                                                Restore
                                            </Button>
                                        ) : isPlayer(u) ? (
                                            <>
                                            {/* Primary 3 — daily-use actions stay inline */}
                                            <Button size="sm" variant="outline" onClick={() => openCreditDialog(u, 'deposit')}
                                                className="border-[#39FF14]/30 text-[#39FF14] hover:bg-[#39FF14]/10 h-8 w-8 p-0"
                                                data-testid={`deposit-btn-${i}`} title="Add credits">
                                                <Plus className="w-4 h-4" />
                                            </Button>
                                            <Button size="sm" variant="outline" onClick={() => openCreditDialog(u, 'withdraw')}
                                                className="border-[#FF3B30]/30 text-[#FF3B30] hover:bg-[#FF3B30]/10 h-8 w-8 p-0"
                                                disabled={!u.credits || u.credits <= 0}
                                                data-testid={`withdraw-btn-${i}`} title="Remove credits">
                                                <Minus className="w-4 h-4" />
                                            </Button>
                                            <Button size="sm" variant="outline" onClick={() => openMessageDialog(u)}
                                                className="border-[#FFD700]/30 text-[#FFD700] hover:bg-[#FFD700]/10 h-8 w-8 p-0"
                                                data-testid={`message-btn-${i}`} title="Send message">
                                                <MessageCircle className="w-4 h-4" />
                                            </Button>

                                            {/* Everything else hides behind a single 3-dot menu */}
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button size="sm" variant="outline"
                                                        className="border-white/10 text-[#A0AAB2] hover:bg-white/5 hover:text-white h-8 w-8 p-0"
                                                        data-testid={`more-btn-${i}`} title="More actions">
                                                        <MoreHorizontal className="w-4 h-4" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end" className="bg-[#0a0a0a] border-white/10 text-white">
                                                    <DropdownMenuLabel className="text-[#A0AAB2] text-[10px] uppercase tracking-wider">Bonuses</DropdownMenuLabel>
                                                    <FreePlayQuickDialog user={u} onGranted={fetchData} trigger={
                                                        <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="cursor-pointer hover:bg-white/5" data-testid={`menu-freeplay-${i}`}>
                                                            <Gift className="w-4 h-4 mr-2 text-[#FF007F]" /> Grant Free Play
                                                        </DropdownMenuItem>
                                                    } />
                                                    <DropdownMenuItem onSelect={() => { setSelectedUser(u); setFreeSpinsDialogOpen(true); }}
                                                        className="cursor-pointer hover:bg-white/5" data-testid={`menu-freespins-${i}`}>
                                                        <Star className="w-4 h-4 mr-2 text-[#FF007F]" /> Free Spins
                                                    </DropdownMenuItem>

                                                    <DropdownMenuSeparator className="bg-white/10" />
                                                    <DropdownMenuLabel className="text-[#A0AAB2] text-[10px] uppercase tracking-wider">Account</DropdownMenuLabel>
                                                    <EditUserDialog user={u} onUpdated={fetchData} trigger={
                                                        <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="cursor-pointer hover:bg-white/5" data-testid={`menu-edit-${i}`}>
                                                            <Pencil className="w-4 h-4 mr-2 text-[#FFD700]" /> Edit Profile
                                                        </DropdownMenuItem>
                                                    } />
                                                    <DropdownMenuItem onSelect={() => { setSelectedUser(u); setResetPwDialogOpen(true); }}
                                                        className="cursor-pointer hover:bg-white/5" data-testid={`menu-resetpw-${i}`}>
                                                        <KeyRound className="w-4 h-4 mr-2 text-[#00BFFF]" /> Reset Password
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem onSelect={() => handleUnlockLogin(u)}
                                                        className="cursor-pointer hover:bg-white/5" data-testid={`menu-unlock-${i}`}>
                                                        <Unlock className="w-4 h-4 mr-2 text-[#39FF14]" /> Unlock Login
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem onSelect={() => { setSelectedUser(u); setStatusDialogOpen(true); }}
                                                        className="cursor-pointer hover:bg-white/5" data-testid={`menu-status-${i}`}>
                                                        <Shield className="w-4 h-4 mr-2 text-[#A0AAB2]" /> Status / Ban
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                            {/* Danger Zone: Archive / Permanently Delete */}
                                            <DangerZoneDialog user={u} onChanged={fetchData} />
                                            </>
                                        ) : (
                                            <span className="text-[#666] text-xs italic px-2">backoffice</span>
                                        )}
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
            {visible.length > 0 && (
                <>
                    <p className="text-[10px] text-[#A0AAB2] text-center mt-2">
                        Showing {Math.min(limit, total)} of {total}
                    </p>
                    <ShowMoreButton testid="users-show-more" />
                </>
            )}
        </>
        )}
    </div>
    );
};
